import {
  captureAutocompleteEmptyResult,
  getCheckoutContext,
} from './sentryEvents'
import { perfEnd, perfStart } from './perfMarks'

/**
 * Поиск места через Google Places по введённому тексту.
 * @param {string}  inputValue  - то, что ввёл пользователь: "Ставрополь, Ставропольский край"
 * @param {Array}   types       - типы из Places API, например ['(regions)']
 * @param {string|string[]} country - ISO-код(ы) страны: 'RU' или ['RU','MD']
 * @param {Function} onResult   - коллбэк, куда отдаём найденный place
 * @param {Object}  options     - дополнительные опции
 * @param {Object}  options.store - Vuex store для извлечения контекста Sentry
 * @param {string}  options.source - источник вызова: 'city' | 'courier_address' | 'post_address'
 */
export async function choosePlace({
  inputValue,
  types = [],
  country,
  onResult = () => {},
  options = {},
}) {
  const { store, source = 'unknown' } = options

  if (!inputValue) return
  if (!window.google?.maps?.places) {
    console.error('Google Maps Places API is not loaded.')
    return
  }

  const startTime = Date.now()

  // готовим сервисы
  const svc = new window.google.maps.places.AutocompleteService()
  // создаём токен сессии для связывания Autocomplete и Place Details.
  // Без токена Google биллит каждый keystroke как Autocomplete (Per Request),
  // с токеном — вся серия + один Place Details считается как одна сессия.
  const sessionToken = new window.google.maps.places.AutocompleteSessionToken()

  // обёртка промиса для Autocomplete
  const getPredictions = (input) =>
    new Promise((resolve, reject) =>
      svc.getPlacePredictions(
        {
          input,
          types,
          // Google принимает и строку, и массив стран
          componentRestrictions: country ? { country } : '',
          sessionToken,
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            resolve(predictions || [])
          } else if (
            status ===
            window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS
          ) {
            resolve([])
          } else {
            reject(
              new Error(
                `Google Places prediction failed with status: ${status}`
              )
            )
          }
        }
      )
    )

  let predictions = []
  let errorType = null

  perfStart('city:suggestions')
  try {
    predictions = (await getPredictions(inputValue)) || []
  } catch (err) {
    errorType = 'timeout'
    console.error('Google Places error:', err)
  } finally {
    perfEnd('city:suggestions')
  }

  const responseTimeMs = Date.now() - startTime

  if (!predictions.length) {
    // Отправляем событие в Sentry о пустом результате
    if (store) {
      captureAutocompleteEmptyResult({
        context: getCheckoutContext(store),
        query: inputValue,
        responseTimeMs,
        errorType: errorType || 'empty',
        source,
      })
    }
    return false
  }

  const ps = new window.google.maps.places.PlacesService(
    document.createElement('div')
  )

  // обёртка для деталей места.
  // fields ограничивает ответ только базовыми полями адреса — без этого
  // Google возвращает все данные и биллит по Contact + Atmosphere SKU.
  // sessionToken связывает этот вызов с серией Autocomplete.
  const getDetails = (placeId) =>
    new Promise((r) =>
      ps.getDetails(
        {
          placeId,
          sessionToken,
          fields: [
            'address_components',
            'formatted_address',
            'place_id',
            'geometry',
          ],
        },
        r
      )
    )

  // детальная инфа по первому совпадению
  const place = await getDetails(predictions[0].place_id)
  onResult(place, true)
}
